import { useState, useRef, useCallback } from 'react'
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { Camera, Upload } from 'lucide-react'
import { Button } from './Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './shadcn/dialog'

interface AvatarUploadProps {
  currentUrl?: string | null
  initials?: string
  onUpload: (file: File) => void
  size?: 'sm' | 'md' | 'lg'
  label?: string
}

const SIZES = {
  sm: { container: 'size-16', text: 'text-lg', icon: 'size-4' },
  md: { container: 'size-20', text: 'text-2xl', icon: 'size-5' },
  lg: { container: 'size-24', text: 'text-3xl', icon: 'size-6' },
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      1, // 1:1 aspect ratio for avatars
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

export function AvatarUpload({
  currentUrl,
  initials,
  onUpload,
  size = 'md',
  label = 'Change photo'
}: AvatarUploadProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState<Crop>()
  const [isUploading, setIsUploading] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const sizeClasses = SIZES[size]

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      setImageSrc(reader.result as string)
      setIsOpen(true)
    }
    reader.readAsDataURL(file)

    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget
    setCrop(centerAspectCrop(width, height))
  }, [])

  const getCroppedImg = async (): Promise<File | null> => {
    const image = imgRef.current
    if (!image || !crop) return null

    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const pixelCrop = {
      x: (crop.x / 100) * image.width * scaleX,
      y: (crop.y / 100) * image.height * scaleY,
      width: (crop.width / 100) * image.width * scaleX,
      height: (crop.height / 100) * image.height * scaleY,
    }

    // Output at 400x400 for high-quality avatars
    const outputSize = 400
    canvas.width = outputSize
    canvas.height = outputSize

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputSize,
      outputSize
    )

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
          } else {
            resolve(null)
          }
        },
        'image/jpeg',
        0.9
      )
    })
  }

  const handleSave = async () => {
    setIsUploading(true)
    try {
      const croppedFile = await getCroppedImg()
      if (croppedFile) {
        onUpload(croppedFile)
        setIsOpen(false)
        setImageSrc(null)
        setCrop(undefined)
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setImageSrc(null)
    setCrop(undefined)
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="flex flex-col items-center gap-3">
        <div
          className={`${sizeClasses.container} rounded-full bg-primary/20 flex items-center justify-center relative group cursor-pointer`}
          onClick={() => inputRef.current?.click()}
        >
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="Avatar"
              className={`${sizeClasses.container} rounded-full object-cover`}
            />
          ) : (
            <span className={`${sizeClasses.text} font-medium text-primary`}>
              {initials || '?'}
            </span>
          )}

          {/* Hover overlay */}
          <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className={`${sizeClasses.icon} text-white`} />
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="cursor-pointer"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="size-4 mr-2" />
          {label}
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Crop your photo</DialogTitle>
            <DialogDescription>
              Drag to reposition and resize the crop area.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-4">
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                aspect={1}
                circularCrop
                className="max-h-[400px]"
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  className="max-h-[400px] w-auto"
                />
              </ReactCrop>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isUploading}
              className="cursor-pointer"
            >
              {isUploading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
